import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Minus, Plus, Star } from 'lucide-react';
import { API_URL } from '../api';
import ProductImage from './ProductImage';
import { buildProductImageSources, isGenericProductVisual, pickPrimaryProductImage } from '../imageFallbacks';

function SetupPackagesSection({
  products = [],
  onAdd,
  onRemove,
  user,
  cartItems = [],
  savedProductIds = [],
  onToggleSaved = null,
  deliveryEtaLabel = '16 mins',
  title = 'Full Setup Packages'
}) {
  const [packages, setPackages] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const [activeProductId, setActiveProductId] = useState(null);
  const navigate = useNavigate();
  const sectionRef = useRef(null);

  const productMap = useMemo(
    () => new Map(products.map((product) => [Number(product.id), product])),
    [products]
  );

  const fallbackSetupProducts = useMemo(() => (
    products
      .filter((product) => {
        const unit = String(product.unit || '').toLowerCase();
        const name = String(product.name || '').toLowerCase();
        const description = String(product.description || '').toLowerCase();
        return unit.includes('setup')
          || name.includes('setup')
          || description.includes('setup package');
      })
      .slice(0, 12)
  ), [products]);

  useEffect(() => {
    let cancelled = false;
    const loadPackages = async () => {
      try {
        const res = await fetch(`${API_URL}/setup-packages`);
        const data = await res.json().catch(() => []);
        if (!cancelled) setPackages(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) setPackages([]);
      }
    };
    loadPackages();
    return () => { cancelled = true; };
  }, []);

  const sectionProducts = useMemo(
    () => {
      const resolved = packages
        .map((entry) => {
          const product = productMap.get(Number(entry.product_id || 0));
          if (!product) return null;
          return {
            ...product,
            name: entry.title || product.name,
            description: entry.subtitle || product.description,
            image: isGenericProductVisual(entry.image) ? product.image : (entry.image || product.image),
            package_badge: entry.badge || '',
            package_price: Number(entry.price || 0) > 0 ? Number(entry.price) : product.price,
            package_category_id: entry.category_id || product.category_id
          };
        })
        .filter(Boolean);
      return resolved.length ? resolved : fallbackSetupProducts;
    },
    [packages, productMap, fallbackSetupProducts]
  );

  if (!sectionProducts.length) return null;

  const stopCardTap = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerAction = (handler) => (event) => {
    stopCardTap(event);
    handler();
  };

  const pulseProduct = (productId) => {
    setActiveProductId(Number(productId));
    window.clearTimeout(pulseProduct.timeoutId);
    pulseProduct.timeoutId = window.setTimeout(() => setActiveProductId(null), 320);
  };

  return (
    <div ref={sectionRef} className={`product-section setup-packages-section home-band home-band-setup motion-section ${isVisible ? 'section-in-view' : ''}`}>
      <div className="section-header">
        <h2 className="section-title">{title}</h2>
        <button
          type="button"
          className="see-all see-all-btn"
          onClick={() => navigate('/shop')}
        >
          See all
        </button>
      </div>
      <div className="product-scroll">
        {sectionProducts.map((product, index) => {
          const cartItem = cartItems.find((item) => Number(item.product_id || item.id) === Number(product.id));
          const selectedQty = cartItem?.quantity || 0;
          const isOutOfStock = Number(product.stock || 0) <= 0;
          const isSaved = savedProductIds.includes(Number(product.id));
          const isAnimating = activeProductId === Number(product.id);
          const discount = Number(product.discount_percent) > 0
            ? Math.round(Number(product.discount_percent))
            : product.mrp ? Math.max(0, Math.round((1 - product.price / product.mrp) * 100)) : 0;
          const basePrice = Number(product.package_price || product.price || 0);
          const rolePrice = user?.role === 'dealer' && Number(product.dealer_price) > 0
            ? Math.round(Number(product.dealer_price))
            : user?.role === 'distributor' && Number(product.distributor_price) > 0
              ? Math.round(Number(product.distributor_price))
              : user?.role === 'dealer' ? Math.round(basePrice * 0.90) : user?.role === 'distributor' ? Math.round(basePrice * 0.85) : basePrice;
          const imageSources = buildProductImageSources(product, { excludeGeneric: true });
          const primaryImage = pickPrimaryProductImage(product, { excludeGeneric: true });

          return (
            <div
              key={product.id}
              className={`product-card motion-card ${isOutOfStock ? 'out-of-stock-card' : ''} ${isAnimating ? 'cart-bump' : ''}`}
              style={{ '--card-stagger': index }}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <div className="product-img-wrap">
                <ProductImage
                  src={primaryImage}
                  sources={imageSources.slice(1)}
                  alt={product.name}
                  loading={index < 3 ? 'eager' : 'lazy'}
                  fetchPriority={index < 2 ? 'high' : 'auto'}
                  fallbackContent="CCTV"
                  proxyWidth={420}
                  proxyQuality={68}
                  proxyFormat="webp"
                  preferDirect={false}
                  fallbackToDirect={false}
                  allowFallbackImage={false}
                  sizes="(max-width: 768px) 72vw, 320px"
                />
                {discount > 0 && (
                  <span className="product-corner-offer" aria-label={`${discount}% off`}>
                    <strong>{discount}%</strong>
                    <small>OFF</small>
                  </span>
                )}
                {onToggleSaved && (
                  <button
                    type="button"
                    className={isSaved ? 'save-item-btn active' : 'save-item-btn'}
                    onPointerDown={handlePointerAction(() => onToggleSaved(product))}
                    onClick={stopCardTap}
                    aria-label={isSaved ? 'Remove from saved items' : 'Save for later'}
                  >
                    <Heart size={16} fill={isSaved ? 'currentColor' : '#ffffff'} />
                  </button>
                )}
                <span className="delivery-badge">{deliveryEtaLabel}</span>
              </div>
              <div className="product-name">{product.name}</div>
              <div className="product-weight">{product.unit}</div>
              <div className="product-rating-row">
                <Star size={13} fill="currentColor" />
                <strong>{Number(product.rating_average || 4.6).toFixed(1)}</strong>
                <span>({Number(product.rating_count || 200)})</span>
              </div>
              <div className="product-price-row blinkit-price-row" onClick={stopCardTap}>
                <span className="product-price-stack">
                  <span className="price-current">Rs {rolePrice}</span>
                  <span className="price-original">Rs {product.mrp}</span>
                </span>
                {isOutOfStock ? (
                  <button className="inline-sold-out-btn" type="button" disabled aria-disabled="true" onClick={stopCardTap}>
                    Sold out
                  </button>
                ) : selectedQty > 0 ? (
                  <div className={`card-qty-stepper inline-stepper ${isAnimating ? 'stepper-bump' : ''}`}>
                    <button type="button" onPointerDown={handlePointerAction(() => { pulseProduct(product.id); onRemove(product, cartItem); })} onClick={stopCardTap}><Minus size={15} /></button>
                    <strong>{selectedQty}</strong>
                    <button type="button" onPointerDown={handlePointerAction(() => { pulseProduct(product.id); onAdd(product); })} onClick={stopCardTap}><Plus size={15} /></button>
                  </div>
                ) : (
                  <button className={`add-btn inline-add-btn ${isAnimating ? 'add-pressed' : ''}`} type="button" onPointerDown={handlePointerAction(() => { pulseProduct(product.id); onAdd(product); })} onClick={stopCardTap}>
                    ADD
                  </button>
                )}
              </div>
              <div className="shop-card-meta-row">
                <span className={`stock-dot ${isOutOfStock ? 'low' : 'ok'}`}>{isOutOfStock ? 'Out of stock' : 'In stock'}</span>
              </div>
              {(user?.role === 'dealer' || user?.role === 'distributor') && <div className="trade-price-note">{user.role} price</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SetupPackagesSection;
